import type { ReactNode } from "react";
import { ROUTE_PERMISSIONS } from "@/shared/constants";
import type { PermissionName } from "@/shared/constants";
import { PermissionRouteGuard } from "./PermissionRouteGuard";

export type RoutePermissionKey = keyof typeof ROUTE_PERMISSIONS;

interface RoutePermissionGuardProps {
  routeKey: RoutePermissionKey;
  children: ReactNode;
  fallbackPath?: string;
}

/**
 * Route guard that applies permission rules from ROUTE_PERMISSIONS.
 */
export function RoutePermissionGuard({
  routeKey,
  children,
  fallbackPath,
}: RoutePermissionGuardProps) {
  const rule = ROUTE_PERMISSIONS[routeKey];

  if ("permission" in rule) {
    return (
      <PermissionRouteGuard
        permission={rule.permission}
        fallbackPath={fallbackPath}
      >
        {children}
      </PermissionRouteGuard>
    );
  }

  if ("anyOf" in rule) {
    return (
      <PermissionRouteGuard
        anyOf={[...rule.anyOf] as PermissionName[]}
        fallbackPath={fallbackPath}
      >
        {children}
      </PermissionRouteGuard>
    );
  }

  return (
    <PermissionRouteGuard
      allOf={[...rule.allOf] as PermissionName[]}
      fallbackPath={fallbackPath}
    >
      {children}
    </PermissionRouteGuard>
  );
}
